import type { LogEntry } from '../types/game';

const MAX_LOG_ENTRIES = 500;

const TYPE_COLORS: Record<LogEntry['type'], string> = {
   info: '#a0a0a0',
   success: '#4eff4e',
   error: '#ff4e4e',
   system: '#ffd700',
   location: '#00e5ff',
   command: '#daa520',
   npc: '#ff69b4',
   item: '#4eff4e',
 };

export class Renderer {
  private outputEl: HTMLElement;
  private statusBarEl: HTMLElement | null;
  private fishingAnimEl: HTMLElement | null;

  constructor(outputEl: HTMLElement, statusBarEl?: HTMLElement | null, fishingAnimEl?: HTMLElement | null) {
    this.outputEl = outputEl;
    this.statusBarEl = statusBarEl || null;
    this.fishingAnimEl = fishingAnimEl || null;
  }

  append(entries: LogEntry[]): void {
    for (const entry of entries) {
      const line = document.createElement('div');
      line.className = `log-entry log-${entry.type}`;
      line.style.color = TYPE_COLORS[entry.type];
      line.textContent = entry.text;
      this.outputEl.appendChild(line);
    }

    while (this.outputEl.children.length > MAX_LOG_ENTRIES) {
      this.outputEl.removeChild(this.outputEl.firstChild!);
    }

    this.scrollToBottom();
  }

  clear(): void {
    this.outputEl.innerHTML = '';
  }

  updateStatusBar(coins: number, locationName: string, inventoryCount: number, isFishing: boolean, playerLevel: number, vesselCount?: number, vesselCap?: number): void {
    if (!this.statusBarEl) return;
    const fishingStatus = isFishing ? '  |  🎣 Casting...' : '';
    const fleetStatus = (vesselCount !== undefined && vesselCount > 0)
      ? `  |  ⚓ ${vesselCount}/${vesselCap ?? vesselCount}`
      : '';
    this.statusBarEl.innerHTML = `
      <span class="status-loc">📍 ${locationName}</span>
      <span class="status-level">⭐ Lv.${playerLevel}</span>
      <span class="status-coins">💰 ${coins.toFixed(2)}</span>
      <span class="status-inv">🐟 ${inventoryCount}</span>
      <span class="status-fleet">${fleetStatus}</span>
      <span class="status-fish">${fishingStatus}</span>
    `;
  }

  showFishingAnimation(show: boolean): void {
    if (!this.fishingAnimEl) return;
    if (show) {
      this.fishingAnimEl.style.display = 'block';
      this.fishingAnimEl.textContent = '';
    } else {
      this.fishingAnimEl.style.display = 'none';
      this.fishingAnimEl.textContent = '';
    }
  }

  showTransitAnimation(destination: string): void {
    if (!this.fishingAnimEl) return;
    this.fishingAnimEl.style.display = 'block';
    this.fishingAnimEl.textContent = `Traveling to ${destination}...`;
  }

  updateTransitAnimation(frame: number): void {
    if (!this.fishingAnimEl) return;
    const arrows = '>'.repeat((frame % 8) + 1);
    const text = this.fishingAnimEl.textContent || '';
    const base = text.replace(/>+$/, '');
    this.fishingAnimEl.textContent = `${base} ${arrows}`;
  }

  hideTransitAnimation(): void {
    if (!this.fishingAnimEl) return;
    this.fishingAnimEl.style.display = 'none';
    this.fishingAnimEl.textContent = '';
  }

  updateFishingAnimation(frame: number): void {
    if (!this.fishingAnimEl) return;
    const waves = '~ '.repeat(frame % 8 + 1);
    this.fishingAnimEl.textContent = `Casting... ${waves}`;
  }

  private scrollToBottom(): void {
    const terminal = this.outputEl.parentElement;
    if (terminal) {
      terminal.scrollTop = terminal.scrollHeight;
    }
  }
}
